import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import MassagePreferenceStep from "@/pages/portal/MassagePreferenceStep";

/**
 * Guest-facing Massage Preferences page (route: /portal/massage-preferences).
 *
 * Iter-369 — the client can now open this any time from My Journey,
 * tune pressure / mood / focus / avoid areas / health flags and hit
 * Save. Every save inserts a fresh row into `db.massage_preferences`
 * on the backend and updates the customer's `latest_massage_preferences_id`
 * pointer, so the check-in wizard prefills the newest answers on the
 * next massage visit — the client only tweaks their "mood of the day"
 * at the desk.
 *
 * Uses the same reusable `MassagePreferenceStep` component that powers
 * the front-desk check-in wizard so both surfaces stay 1:1.
 */

const DEFAULT_FORM = {
  pressure: "medium",
  mood_physically: 5,
  mood_emotionally: 5,
  avoid_areas: [],
  focus_areas: [],
  health_conditions: {},
  agreed_disclaimer: false,
};

export default function CustomerMassagePreferences() {
  const nav = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customerApi.get("/customers/me/massage-preferences");
      const p = r.data?.preferences || null;
      if (p) {
        setForm({
          pressure: p.pressure || "medium",
          mood_physically: typeof p.mood_physically === "number" ? p.mood_physically : 5,
          mood_emotionally: typeof p.mood_emotionally === "number" ? p.mood_emotionally : 5,
          avoid_areas: Array.isArray(p.avoid_areas) ? p.avoid_areas : [],
          focus_areas: Array.isArray(p.focus_areas) ? p.focus_areas : [],
          health_conditions:
            p.health_conditions && typeof p.health_conditions === "object" ? p.health_conditions : {},
          // Force a fresh Spa Treatment Release tick on every save so the
          // legal capture is explicit each time (matches the check-in rule).
          agreed_disclaimer: false,
        });
        setSavedAt(p.created_at || null);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status && status !== 404) toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.agreed_disclaimer) {
      toast.error("Please agree to the Spa Treatment Release to save.");
      return;
    }
    setBusy(true);
    try {
      const r = await customerApi.put("/customers/me/massage-preferences", form);
      const p = r.data?.preferences || {};
      setSavedAt(p.created_at || new Date().toISOString());
      toast.success("Saved · your therapist will see this on your next massage visit");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const fmtBali = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("en-GB", {
        timeZone: "Asia/Makassar",
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch { return null; }
  };

  return (
    <div>
      <CustomerNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <button
          type="button"
          onClick={() => nav("/portal/profile")}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4"
          data-testid="massage-prefs-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to profile
        </button>
        <p className="tracking-overline text-[11px] text-muted-foreground">MASSAGE</p>
        <h1
          className="font-editorial text-4xl sm:text-5xl mt-2 sm:mt-3 mb-2"
          data-testid="massage-prefs-heading"
        >
          Massage preferences
        </h1>
        <p className="text-sm text-muted-foreground mb-2 max-w-xl">
          Set your pressure, mood, focus and areas to avoid ahead of time. When you check in for a
          massage the therapist will already know what you like — you can still tweak anything on
          the day.
        </p>
        {savedAt && (
          <p
            className="text-[11px] text-muted-foreground mb-6"
            data-testid="massage-prefs-last-update"
          >
            Last saved {fmtBali(savedAt)}
          </p>
        )}

        <Card className="rounded-2xl">
          <CardContent className="p-5 sm:p-8">
            {loading ? (
              <div className="text-sm text-muted-foreground" data-testid="massage-prefs-loading">
                Loading…
              </div>
            ) : (
              <>
                <MassagePreferenceStep value={form} onChange={setForm} />
                <div className="flex justify-end mt-6">
                  <Button
                    className="rounded-full"
                    onClick={save}
                    disabled={busy || !form.agreed_disclaimer}
                    data-testid="massage-prefs-save-btn"
                  >
                    {busy ? "Saving…" : "Save preferences"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
